/**
 * The "Loading" / "Ad" / "Content" badge overlaid on the player (CESP-696).
 *
 * The badge exists so a reviewer watching an evidence video can tell an ad
 * segment from publisher content without reading the beacon log. "Loading"
 * is the third phase: without it a reviewer cannot tell a player that is
 * still buffering from one that is already showing the ad, so the frames
 * before playback starts get mis-read as ad frames. Two things make this
 * harder than setting `textContent`:
 *
 *   1. The MRC-pin layouts (`main.js`, CESP-685) push part of the player off
 *      the viewport. A badge anchored to the player's own top-right corner
 *      leaves the frame entirely for `cut=top` and `cut=right`, so exactly
 *      the scenarios that need the most explaining record no label at all.
 *      `computeBadgeInset` re-anchors it to the top-right of the VISIBLE
 *      slice instead.
 *   2. The badge must never intercept a pointer event - IMA's Skip button
 *      lives in the same overlay and Playwright aborts a click when another
 *      element receives it. The badge is `pointer-events: none` in CSS; this
 *      module only moves it, so that guarantee is unaffected even when the
 *      badge ends up near the ad chrome on a heavily cut player.
 */

/**
 * Playback phase the badge reports.
 *
 * `loading` covers every moment where neither segment is on screen yet: page
 * load up to the first frame, and the gap between an ad break ending and the
 * content actually resuming. Callers move to `content` off the content
 * element's own playback signal, never off the IMA callback that merely asks
 * for a resume, so the label never claims content that is still buffering.
 *
 * @typedef {'loading' | 'ad' | 'content'} BadgePhase
 */

/** @type {BadgePhase} */
export const BADGE_PHASE_LOADING = 'loading';
/** @type {BadgePhase} */
export const BADGE_PHASE_AD = 'ad';
/** @type {BadgePhase} */
export const BADGE_PHASE_CONTENT = 'content';

/** Visible label text. iOS pads via spaces in UILabel; web uses {@link BADGE_PADDING}. */
export const BADGE_LABEL_LOADING = 'Loading';
export const BADGE_LABEL_AD = 'Ad';
export const BADGE_LABEL_CONTENT = 'Content';

/** Gap between the badge and the edge it is anchored to, in CSS pixels. */
export const BADGE_MARGIN_PX = 8;

export const BADGE_BORDER_RADIUS_PX = 8;

/** Inner padding — mirrors the inset iOS gets from spaced labels + sizeToFit. */
export const BADGE_PADDING_Y_PX = 4;
export const BADGE_PADDING_X_PX = 10;
export const BADGE_PADDING = `${BADGE_PADDING_Y_PX}px ${BADGE_PADDING_X_PX}px`;

/** UIColor.systemGray / systemRed / systemBlue with 0.85 alpha. */
export const BADGE_COLOR_LOADING = 'rgba(142, 142, 147, 0.85)';
export const BADGE_COLOR_AD = 'rgba(255, 59, 48, 0.85)';
export const BADGE_COLOR_CONTENT = 'rgba(0, 122, 255, 0.85)';

/**
 * Label, colour and modifier class per phase. Keyed so an unknown phase can
 * fall back rather than blanking the badge mid-recording.
 *
 * @type {Record<BadgePhase, { label: string, color: string, className: string }>}
 */
const BADGE_PHASES = {
  [BADGE_PHASE_LOADING]: {
    label: BADGE_LABEL_LOADING,
    color: BADGE_COLOR_LOADING,
    className: 'is-loading',
  },
  [BADGE_PHASE_AD]: { label: BADGE_LABEL_AD, color: BADGE_COLOR_AD, className: 'is-ad' },
  [BADGE_PHASE_CONTENT]: {
    label: BADGE_LABEL_CONTENT,
    color: BADGE_COLOR_CONTENT,
    className: 'is-content',
  },
};

/** Modifier classes the badge owns, so a phase change clears the previous one. */
const BADGE_CLASSES = Object.values(BADGE_PHASES).map((phase) => phase.className);

/**
 * Size assumed when the badge cannot be measured (not laid out yet).
 * Only feeds the margin clamp, so being a few pixels off is harmless.
 */
export const BADGE_FALLBACK_SIZE = { width: 72, height: 26 };

/**
 * Set the badge text and colour for the current playback phase.
 *
 * @param {{ textContent: string, classList: { toggle: (c: string, on: boolean) => void } } | null} badge
 *        Null when the element is missing from the page - callers don't need to guard.
 * @param {BadgePhase} phase
 */
export function setBadgePhase(badge, phase) {
  if (!badge) return;
  const style = BADGE_PHASES[phase] ?? BADGE_PHASES[BADGE_PHASE_LOADING];
  badge.textContent = style.label;
  badge.style.backgroundColor = style.color;
  badge.style.padding = BADGE_PADDING;
  for (const className of BADGE_CLASSES) {
    badge.classList.toggle(className, className === style.className);
  }
}

/**
 * Shrink the margin so the badge still fits inside the visible slice.
 * Returns 0 when the slice is shorter than the badge itself: the badge then
 * starts flush with the viewport edge, which is the most of it that can be
 * recorded at all.
 *
 * @param {number} visibleExtent - Visible player size along the cut axis, px.
 * @param {number} badgeExtent   - Badge size along the same axis, px.
 * @returns {number}
 */
function clampMargin(visibleExtent, badgeExtent) {
  return Math.max(0, Math.min(BADGE_MARGIN_PX, visibleExtent - badgeExtent));
}

/**
 * Offsets that place the badge in the top-right corner of the part of the
 * player that is actually inside the viewport.
 *
 * `cut=bottom` and `cut=left` hide the bottom / left of the player, which
 * never contains the badge, so they keep the plain CSS margins. `cut=top`
 * and `cut=right` hide the badge's own corner and are pushed inwards by the
 * hidden amount.
 *
 * @param {object} geometry
 * @param {number} geometry.playerWidth  - Pinned player width, px.
 * @param {number} geometry.playerHeight - Pinned player height, px.
 * @param {number} geometry.visiblePct   - Percentage of the player left in view.
 * @param {'bottom'|'top'|'left'|'right'} geometry.cut - Viewport edge doing the cut.
 * @param {number} [geometry.badgeWidth]  - Measured badge width, px.
 * @param {number} [geometry.badgeHeight] - Measured badge height, px.
 * @returns {{ top: number, right: number }} Insets from the player's top-right corner.
 */
export function computeBadgeInset({
  playerWidth,
  playerHeight,
  visiblePct,
  cut,
  badgeWidth = BADGE_FALLBACK_SIZE.width,
  badgeHeight = BADGE_FALLBACK_SIZE.height,
}) {
  const fallback = { top: BADGE_MARGIN_PX, right: BADGE_MARGIN_PX };
  const fraction = Math.max(0, Math.min(100, Number(visiblePct))) / 100;
  if (!Number.isFinite(fraction)) return fallback;

  if (cut === 'top') {
    if (!Number.isFinite(playerHeight) || playerHeight <= 0) return fallback;
    const hidden = playerHeight * (1 - fraction);
    return {
      top: Math.round(hidden + clampMargin(playerHeight - hidden, badgeHeight)),
      right: BADGE_MARGIN_PX,
    };
  }

  if (cut === 'right') {
    if (!Number.isFinite(playerWidth) || playerWidth <= 0) return fallback;
    const hidden = playerWidth * (1 - fraction);
    return {
      top: BADGE_MARGIN_PX,
      right: Math.round(hidden + clampMargin(playerWidth - hidden, badgeWidth)),
    };
  }

  return fallback;
}

/**
 * Measure the badge, compute its inset and write it to the element.
 *
 * @param {HTMLElement | null} badge
 * @param {{ playerWidth: number, playerHeight: number, visiblePct: number,
 *           cut: 'bottom'|'top'|'left'|'right' }} geometry
 * @returns {{ top: number, right: number } | null} Applied inset, null when there is no badge.
 */
export function applyBadgeInset(badge, geometry) {
  if (!badge) return null;
  const inset = computeBadgeInset({
    ...geometry,
    badgeWidth: badge.offsetWidth || BADGE_FALLBACK_SIZE.width,
    badgeHeight: badge.offsetHeight || BADGE_FALLBACK_SIZE.height,
  });
  badge.style.top = `${inset.top}px`;
  badge.style.right = `${inset.right}px`;
  return inset;
}

/**
 * Phase state machine behind the badge, so the four test apps agree on when
 * each label is shown rather than each re-deriving it from its own SDK events.
 *
 * The badge names what is on the player surface right now, never what the ad
 * lifecycle has reached: an earlier version promoted to `content` only once an
 * ad break had started or ended, which left every format whose break comes
 * after the content - mid-roll above all - labelled "Loading" over content
 * that was visibly playing (CESP-808).
 *
 * `loading` is what covers a surface with nothing on it: page load before the
 * first frame, the "Loading Ad..." scrim while IMA fetches VAST (the content
 * element autoplays under it, see docs/content-video-autoplay-during-ad-wait.md),
 * the cue-to-first-frame gap at the head of a break, and the gap between a
 * break ending and content coming back. A nonlinear overlay never takes the
 * surface, so it never moves the badge off `content`.
 *
 * @param {Parameters<typeof setBadgePhase>[0]} badge
 */
export function createBadgeController(badge) {
  /** A break holds the surface: cued, rendering, or between the two. */
  let adOwnsSurface = false;
  /** The break on the surface has a frame up rather than being merely cued. */
  let adRendering = false;
  /** The "Loading Ad..." scrim covers the player. */
  let adLoadingUp = false;
  /** Content has frames on screen: set by `playing`, cleared by a break. */
  let contentOnScreen = false;

  const render = () => {
    if (adOwnsSurface) {
      setBadgePhase(badge, adRendering ? BADGE_PHASE_AD : BADGE_PHASE_LOADING);
      return;
    }
    const contentVisible = contentOnScreen && !adLoadingUp;
    setBadgePhase(badge, contentVisible ? BADGE_PHASE_CONTENT : BADGE_PHASE_LOADING);
  };

  render();

  return {
    /**
     * The "Loading Ad..." scrim went up or came down. While it is up the
     * scrim, not the content element, is what the viewer sees.
     *
     * @param {boolean} loading
     */
    adLoadingChanged(loading) {
      adLoadingUp = loading;
      render();
    },
    /** IMA cued a break: the ad owns the surface but has no frame up yet. */
    adBreakStarting() {
      adOwnsSurface = true;
      adRendering = false;
      contentOnScreen = false;
      render();
    },
    /** The ad is really on screen (IMA's `STARTED`). */
    adStarted() {
      adOwnsSurface = true;
      adRendering = true;
      contentOnScreen = false;
      render();
    },
    /** The ad break is over, errored, or never happened. */
    adEnded() {
      // Only a break that actually held the surface leaves the player empty
      // behind it. A nonlinear overlay never took content off screen, so its
      // end must not re-label content that never stopped playing.
      if (adOwnsSurface) contentOnScreen = false;
      adOwnsSurface = false;
      adRendering = false;
      render();
    },
    /** The content element reported real playback (its `playing` event). */
    contentStarted() {
      contentOnScreen = true;
      render();
    },
  };
}
